import TasksAPI from 'dashboard/api/tasks';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    count: 0,
    currentPage: 1,
    totalPages: 1,
  },
  stats: {
    openCount: 0,
    inProgressCount: 0,
    inReviewCount: 0,
    doneCount: 0,
    cancelledCount: 0,
    overdueCount: 0,
    waitingExternalCount: 0,
  },
};

const getters = {
  getTasks: $state => $state.records,
  getTaskUIFlags: $state => $state.uiFlags,
  getTaskMeta: $state => $state.meta,
  getTaskStats: $state => $state.stats,
  getTaskByDisplayId: $state => displayId =>
    $state.records.find(t => Number(t.display_id) === Number(displayId)),
};

const actions = {
  async fetchTasks({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TasksAPI.get(params);
      commit('setTasks', response.data.payload);
      commit('setMeta', response.data.meta);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTask({ commit }, { id }) {
    commit('setUIFlag', { isFetchingItem: true });
    try {
      const response = await TasksAPI.show(id);
      commit('addOrUpdateTask', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingItem: false });
    }
  },

  async fetchMeta({ commit }) {
    const response = await TasksAPI.meta();
    commit('setStats', response.data.meta);
    return response.data;
  },

  async createTask({ commit }, taskData) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await TasksAPI.create(taskData);
      commit('addOrUpdateTask', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateTask({ commit }, { id, ...taskData }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await TasksAPI.update(id, taskData);
      commit('addOrUpdateTask', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteTask({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await TasksAPI.delete(id);
      commit('removeTask', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async assignTask({ commit }, { id, assigneeId }) {
    const response = await TasksAPI.assign(id, assigneeId);
    commit('addOrUpdateTask', response.data);
    return response.data;
  },

  async addBlocker({ commit }, { id, blockerDisplayId }) {
    const response = await TasksAPI.addBlocker(id, blockerDisplayId);
    commit('addOrUpdateTask', response.data);
    return response.data;
  },

  async removeBlocker({ commit }, { id, blockerDisplayId }) {
    const response = await TasksAPI.removeBlocker(id, blockerDisplayId);
    commit('addOrUpdateTask', response.data);
    return response.data;
  },
};

const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  setTasks($state, tasks) {
    $state.records = tasks || [];
  },
  setMeta($state, meta) {
    $state.meta = {
      count: meta.count || 0,
      currentPage: meta.current_page || 1,
      totalPages: meta.total_pages || 1,
    };
  },
  setStats($state, stats) {
    $state.stats = {
      openCount: stats.open_count || 0,
      inProgressCount: stats.in_progress_count || 0,
      inReviewCount: stats.in_review_count || 0,
      doneCount: stats.done_count || 0,
      cancelledCount: stats.cancelled_count || 0,
      overdueCount: stats.overdue_count || 0,
      waitingExternalCount: stats.waiting_external_count || 0,
    };
  },
  addOrUpdateTask($state, task) {
    if (!task || !task.id) return;
    const index = $state.records.findIndex(t => t.id === task.id);
    if (index === -1) {
      $state.records = [task, ...$state.records];
    } else {
      const next = [...$state.records];
      next[index] = task;
      $state.records = next;
    }
  },
  removeTask($state, id) {
    $state.records = $state.records.filter(t => t.id !== id);
  },
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
